import type { FC } from 'react'
import { useMutation } from '~/shared/hooks/promiseState'
import { useAuth0 } from '@auth0/auth0-react'
import { Box, Button, Fade, Grow, Typography } from '@mui/material'
import { Button as GenericButton, Loader } from '~/shared/ui'
import STARBURST from '~/assets/images/starburst.png'
import LOGO from '~/assets/images/logo_full.png'
import { logger } from '~/shared/lib/logger'
import { decodeSafeInternalPath } from '~/shared/lib/safeInternalPath'

const Login: FC = () => {
  const auth0 = useAuth0()
  const auth0Login = (orgId: string) => {
    // Use Auth0 appState to persist state
    // so that after authentication, we can redirect to the last known location
    // Previous location is stored in the "next" URL query param
    const nextUrl = location.search.split('?next=')[1]
    logger.debug('[Session] auth0Login with organization ID', orgId)
    return auth0.loginWithRedirect({
      appState: { next: decodeSafeInternalPath(nextUrl) },
      authorizationParams: {
        organization: orgId,
      },
    })
  }
  const { run, loading } = useMutation(() => auth0Login(import.meta.env.VITE_AUTH0_ORG_ID))
  const { run: partnerHandler, loading: partnerLoading } = useMutation(() =>
    auth0Login(import.meta.env.VITE_AUTH0_PARTNER_ORG_ID),
  )

  return (
    <Box
      sx={{
        background: `url(${STARBURST}) left bottom no-repeat`,
        backgroundSize: '25%',
        position: 'absolute',
        height: '100%',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Explicit responsive width (mirrors the old Grid columns xs=11/sm=5/md=3/
          lg=2 of 12). A bare <Grid item> without a <Grid container> no longer caps
          its width in MUI v5, which blew the logo up to full width. */}
      <Box sx={{ width: { xs: '91.67%', sm: '41.67%', md: '25%', lg: '16.67%' } }}>
        <Grow in>
          <Fade in>
            <Box sx={{ mt: -5 }}>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                <img src={LOGO} alt="Logo" width="100%" />
              </Box>
              <Button
                disabled={auth0.isLoading || loading}
                data-testid="login-button"
                variant="contained"
                fullWidth
                onClick={run}
                sx={{
                  bgcolor: 'navy.500',
                  color: 'common.white',
                  '&:hover': { bgcolor: 'navy.900' },
                }}
              >
                {auth0.isLoading || loading ? <Loader inline /> : 'LOG IN'}
              </Button>
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" align="center">
                  DFCI partner providers,&nbsp;
                  <GenericButton
                    onClick={partnerHandler}
                    disabled={auth0.isLoading || partnerLoading}
                  >
                    <Typography variant="body2" sx={{ color: 'primary.main' }}>
                      log in here
                    </Typography>
                  </GenericButton>
                </Typography>
              </Box>
            </Box>
          </Fade>
        </Grow>
      </Box>
    </Box>
  )
}

export default Login
